import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from './AppShell.js';
import { PublicHeader } from './chrome.js';
import { Button, buttonClassName } from '../primitives/index.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { ThemeToggle } from './ThemeToggle.js';

function AccountActions(): ReactElement {
  const { account, logout } = useAuth();
  return (
    <span className="topbar__actions">
      <ThemeToggle />
      {account === null ? (
        /*
          Sign IN stays at every width: it is the one conversion the public
          pages exist for, and a visitor has no other route to it.
        */
        <Link to="/login" className={buttonClassName('primary', 'sm')}>
          Sign in
        </Link>
      ) : (
        /*
          Sign OUT steps out of the topbar on a phone.
          
          The brand, the seat badge, the theme toggle and an 81px button did not
          fit a 360px bar: every authenticated page scrolled sideways and the
          button itself was partly off-screen. Signing out is not something a
          person does often, it already has its own section on the Account
          screen, and Account is one tap away in the tab bar — so this is the
          element that gives way, rather than the product's name or the signal
          telling you which half of it you are in.

          Wrapped rather than classed: `Button` deliberately omits `className`
          so a caller cannot restyle it. Hiding it is a LAYOUT decision that
          belongs to the bar, not a change to the button.
        */
        <span className="topbar__signout">
          <Button variant="ghost" size="sm" onClick={() => void logout()}>
            Sign out
          </Button>
        </span>
      )}
    </span>
  );
}

export function PublicShell(): ReactElement {
  return <AppShell header={<PublicHeader actions={<AccountActions />} />} />;
}

export function CustomerShell(): ReactElement {
  return (
    <AppShell
      seat="customer"
      sidebar={{
        label: 'Customer',
        groups: [
          {
            label: 'Rentals',
            items: [
              { to: '/app', label: 'Overview', end: true },
              { to: '/app/bookings', label: 'Bookings' },
              { to: '/app/offers', label: 'Offers' },
            ],
          },
          {
            label: 'You',
            items: [{ to: '/app/account', label: 'Account' }],
          },
        ],
      }}
      tabs={[
        { to: '/app/bookings', label: 'Bookings' },
        { to: '/app/offers', label: 'Offers' },
        { to: '/equipment', label: 'Browse' },
        { to: '/app/account', label: 'Account' },
      ]}
      actions={<AccountActions />}
    />
  );
}

export function OwnerShell(): ReactElement {
  return (
    <AppShell
      seat="owner"
      sidebar={{
        label: 'Owner',
        groups: [
          {
            label: 'Fleet',
            items: [
              { to: '/owner', label: 'Overview', end: true },
              { to: '/owner/equipment', label: 'Equipment' },
            ],
          },
          {
            label: 'Business',
            items: [
              { to: '/owner/offers', label: 'Offers' },
              { to: '/owner/payouts', label: 'Payouts' },
            ],
          },
        ],
      }}
      tabs={[
        { to: '/owner/equipment', label: 'Fleet' },
        { to: '/owner/offers', label: 'Offers' },
        { to: '/owner', label: 'Overview', end: true },
        { to: '/app/account', label: 'Account' },
      ]}
      actions={<AccountActions />}
    />
  );
}

/**
 * Support console, not an admin suite. Nine of ~12 admin endpoints are blocked,
 * so the navigation offers exactly what exists: read-only oversight and
 * identity correction.
 */
export function SupportShell(): ReactElement {
  return (
    <AppShell
      seat="support"
      sidebar={{
        label: 'Support',
        groups: [
          {
            label: 'Oversight',
            items: [{ to: '/support', label: 'Booking lookup', end: true }],
          },
        ],
      }}
      actions={<AccountActions />}
    />
  );
}
